import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { Activity } from "@ckgo2/shared/types";
import {
  Plus,
  Search,
  Activity as ActivityIcon,
  Edit,
  Trash2,
  Copy,
  MapPin,
  Clock,
} from "lucide-react";

interface ActivityWithDest extends Activity {
  destinationName?: string;
}

const TYPE_LABELS: Record<string, string> = {
  water: "Water",
  adventure: "Adventure",
  culture: "Culture",
  nature: "Nature",
  food: "Food",
  wellness: "Wellness",
};

export default function ActivitiesPage() {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["activities"],
    queryFn: () => api.get<ApiResponse<ActivityWithDest[]>>("/api/admin/inspirations"),
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) =>
      api.post<ApiResponse<ActivityWithDest>>(`/api/admin/inspirations/${id}/duplicate`),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      if (response.data?.id) {
        navigate(`/inspirations/${response.data.id}`);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/inspirations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
    },
  });

  const handleDelete = (id: string, title: string) => {
    if (confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  };

  const activities = data?.data || [];

  const filteredActivities = activities.filter((a) => {
    const matchesSearch =
      a.title.toLowerCase().includes(search.toLowerCase()) ||
      a.location?.toLowerCase().includes(search.toLowerCase());
    const matchesType = !typeFilter || a.activityType === typeFilter;
    const matchesStatus = !statusFilter || a.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  if (error) {
    return (
      <div className="card text-error-600 text-center">
        Error loading activities. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Inspirations</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">Manage inspirations</p>
        </div>
        <Link to="/inspirations/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          New inspiration
        </Link>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search inspirations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="input w-full sm:w-48"
          >
            <option value="">All types</option>
            {Object.entries(TYPE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input w-full sm:w-48"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-gray-500">
            <ActivityIcon className="mb-2 h-12 w-12 text-gray-300" />
            <p>No inspirations found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Activity
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Type
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Destination
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Duration
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredActivities.map((activity) => (
                <tr key={activity.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {activity.featuredImage ? (
                        <img
                          src={activity.featuredImage}
                          alt={activity.title}
                          className="h-10 w-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
                          <ActivityIcon className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {activity.title}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">/{activity.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {activity.activityType ? (
                      <span className="bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 rounded px-2 py-0.5 text-xs font-medium">
                        {TYPE_LABELS[activity.activityType] || activity.activityType}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {activity.destinationName ? (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-300">
                          {activity.destinationName}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {activity.duration ? (
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-300">
                          {activity.duration}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {activity.status === "published" ? (
                      <span className="badge bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
                        Published
                      </span>
                    ) : activity.status === "draft" ? (
                      <span className="badge bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400">
                        Draft
                      </span>
                    ) : (
                      <span className="badge bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        Archived
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => duplicateMutation.mutate(activity.id)}
                        disabled={duplicateMutation.isPending}
                        className="btn-ghost btn-sm"
                        title="Duplicate activity"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <Link
                        to={`/inspirations/${activity.id}`}
                        className="btn-ghost btn-sm"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </Link>
                      <button
                        onClick={() => handleDelete(activity.id, activity.title)}
                        disabled={deleteMutation.isPending}
                        className="btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
